import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { findChainPredecessor, findChainSuccessor, openLastDatedPrices } from "components/Title/PriceHistory/utils";
import type { Currencies } from "store/settingsStore";

import type { IPriceHistoryEntry } from ".";

export interface PriceHistoryState {
    draftEntries: IPriceHistoryEntry[];
    isDraft: boolean;
}

const initialState: PriceHistoryState = {
    draftEntries: [],
    isDraft: false,
};

export const priceHistorySlice = createSlice({
    name: "priceHistory",
    initialState,
    reducers: {
        /** Copy server entries into the draft — call when entering edit mode */
        initDraft(state, action: PayloadAction<IPriceHistoryEntry[]>) {
            state.draftEntries = openLastDatedPrices(action.payload);
            state.isDraft = true;
        },
        /** Discard all pending changes — call on form cancel / unmount */
        discardDraft(state) {
            state.draftEntries = [];
            state.isDraft = false;
        },
        addDraftEntry(state, action: PayloadAction<Omit<IPriceHistoryEntry, "id" | "createdAt" | "updatedAt">>) {
            const tempId = -Date.now(); // not yet persisted on server
            state.draftEntries.push({
                ...action.payload,
                id: tempId,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            });
        },
        addDefaultEntry(
            state,
            action: PayloadAction<{
                channelId?: number;
                titleId?: number;
                currency?: Currencies;
                price?: string | number;
            }>,
        ) {
            if (state.draftEntries.filter((d) => d.channelId === action.payload.channelId).length > 0) {
                return;
            }
            // The default price carries no dates at all: titles are sometimes created
            // after sales already happened, so it must cover all time (shown as "—").
            state.draftEntries = [
                ...state.draftEntries,
                {
                    childCurrencies: [],
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    currency: action.payload.currency ?? "EUR",
                    endDate: null,
                    startDate: null,
                    id: -Date.now(),
                    price: Math.round(+(action.payload.price?.toString().replace(",", ".") || "0") * 100) ?? 0,
                    channelId: action.payload.channelId,
                    titleId: action.payload.titleId,
                },
            ];
        },
        updateDraftEntry(state, action: PayloadAction<IPriceHistoryEntry>) {
            const idx = state.draftEntries.findIndex((e) => e.id === action.payload.id);
            if (idx !== -1) {
                state.draftEntries[idx] = { ...action.payload, updatedAt: new Date().toISOString() };
            }
        },
        deleteDraftEntry(state, action: PayloadAction<number>) {
            const entryToDelete = state.draftEntries.find((e) => e.id === action.payload);
            if (
                !entryToDelete ||
                state.draftEntries.filter((d) => d.channelId === entryToDelete.channelId).length <= 1
            ) {
                return;
            }
            state.draftEntries = state.draftEntries.filter((e) => e.id !== action.payload);
            // Periods form a contiguous chain per (channelId, currency): a neighbour
            // absorbs the deleted range so no gap ever appears. The previous period
            // takes over the deleted end (reopening when it was open-ended); when
            // there is no previous period, the next one starts where the deleted did.
            const prev = findChainPredecessor(state.draftEntries, entryToDelete);
            if (prev) {
                prev.endDate = entryToDelete.endDate;
            } else {
                const next = findChainSuccessor(state.draftEntries, entryToDelete);
                if (next) {
                    next.startDate = entryToDelete.startDate;
                }
            }
        },
    },
});

export const { initDraft, discardDraft, addDraftEntry, updateDraftEntry, deleteDraftEntry, addDefaultEntry } =
    priceHistorySlice.actions;

export default priceHistorySlice.reducer;
